import { styled } from '@linaria/react';
import { useTranslation } from 'react-i18next';
import type { OutcomeOverviewMetrics } from '../../../../api/strategy-client/strategy-client.type';
import { primaryBase, redBase, skyDark, skyLight, skyTight, yellowBase } from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';

type OverviewInfoCardsProps = {
  metrics: OutcomeOverviewMetrics | undefined;
  overallProgress: number;
  entityType: 'outcome' | 'goal';
};

type HealthData = {
  on_track: number;
  at_risk: number;
  off_track: number;
  unknown: number;
};

// Narrow union of the 8 tooltip-key suffixes this component resolves. Keeps
// the template-literal key `strategy.overview.tooltips.<CardId>` provably
// present in the dictionary (all 8 entries exist under overview.tooltips) —
// any new card whose id is not in this union is a TS error, not a silent
// Layer-2-only runtime catch.
type CardId =
  | 'overall_progress'
  | 'active_initiatives'
  | 'throughput'
  | 'cycle_time'
  | 'risk_score'
  | 'team_count'
  | 'epic_count'
  | 'initiative_health';

type CardData = {
  id: CardId;
  name: string;
  value: string | number;
  description: string;
  isHealthCard?: boolean;
  healthData?: HealthData;
};

export function OverviewInfoCards({ metrics, overallProgress, entityType }: OverviewInfoCardsProps) {
  const i18n = useTranslation();

  // Single-cast `t` against its plain (key, options) signature to dodge an
  // i18next v26 overload-resolution quirk: with a narrow literal key union,
  // the typed `t` selects the `(key, defaultValue, options)` overload and
  // rejects the Record in the 2nd position. Casting the function reference
  // picks the plain overload; the `CardId` union above still guards every
  // call site, and all 8 dictionary values are strings so the `string`
  // return is safe.
  const tSimple = i18n.t as (key: string, opts?: Record<string, unknown>) => string;

  if (!metrics) {
    return null;
  }

  // Get the correct entity name based on type
  const entityName =
    entityType === 'outcome' ? i18n.t('common.outcome').toLowerCase() : i18n.t('common.strategic_goal').toLowerCase();

  // Safely extract initiative_health with defaults
  const initiativeHealth = metrics.initiative_health || {
    on_track: 0,
    at_risk: 0,
    off_track: 0,
    unknown: 0,
  };

  const cards: CardData[] = [
    {
      id: 'overall_progress',
      name: i18n.t('strategy.overview.info_cards.overall_progress'),
      value: `${overallProgress}%`,
      description: i18n.t('common.progress'),
    },
    {
      id: 'active_initiatives',
      name: i18n.t('strategy.overview.info_cards.active_initiatives'),
      value: metrics.active_initiatives_count ?? 0,
      description: i18n.t('strategy.overview.info_cards.active_initiatives'),
    },
    {
      id: 'throughput',
      name: i18n.t('strategy.overview.info_cards.last_30_days_throughput'),
      value: metrics.last_30_days_throughput ?? 0,
      description: i18n.t('common.work_units'),
    },
    {
      id: 'cycle_time',
      name: i18n.t('strategy.overview.info_cards.last_30_days_cycle_time'),
      value: (metrics.last_30_days_cycle_time ?? 0) > 0 ? `${metrics.last_30_days_cycle_time}` : '0',
      description: i18n.t('strategy.overview.info_cards.days'),
    },
    {
      id: 'risk_score',
      name: i18n.t('strategy.overview.info_cards.average_risk_score'),
      value: (metrics.average_risk_score ?? 0) > 0 ? metrics.average_risk_score.toFixed(1) : '0',
      description: 'Out of 10',
    },
    {
      id: 'team_count',
      name: i18n.t('strategy.overview.info_cards.team_count'),
      value: metrics.team_count ?? 0,
      description: i18n.t('common.teams'),
    },
    {
      id: 'epic_count',
      name: i18n.t('strategy.overview.info_cards.epic_count'),
      value: metrics.epic_count ?? 0,
      description: i18n.t('common.epics'),
    },
    {
      id: 'initiative_health',
      name: i18n.t('strategy.overview.info_cards.initiative_health'),
      value: '',
      description: '',
      isHealthCard: true,
      healthData: initiativeHealth,
    },
  ];

  return (
    <SectionContainer>
      <MeasuresGrid>
        {cards.map((card) => {
          if (card.isHealthCard && card.healthData) {
            return (
              <MeasureCard key={card.id} $clickable={false}>
                <MeasureHeader>
                  <MeasureTitle>{card.name}</MeasureTitle>
                  <Tooltip variant="black" label={i18n.t('strategy.overview.tooltips.initiative_health')} zIndex={1000}>
                    <Icon name="info" size={16} color={skyDark} style={{ cursor: 'help' }} />
                  </Tooltip>
                </MeasureHeader>
                <HealthBreakdown>
                  <HealthItem color={primaryBase}>
                    <HealthLabel>{i18n.t('strategy.overview.info_cards.on_track')}</HealthLabel>
                    <HealthValue>{card.healthData.on_track}</HealthValue>
                  </HealthItem>
                  <HealthItem color={yellowBase}>
                    <HealthLabel>{i18n.t('strategy.overview.info_cards.at_risk')}</HealthLabel>
                    <HealthValue>{card.healthData.at_risk}</HealthValue>
                  </HealthItem>
                  <HealthItem color={redBase}>
                    <HealthLabel>{i18n.t('strategy.overview.info_cards.off_track')}</HealthLabel>
                    <HealthValue>{card.healthData.off_track}</HealthValue>
                  </HealthItem>
                  <HealthItem color={skyDark}>
                    <HealthLabel>{i18n.t('strategy.overview.info_cards.unknown')}</HealthLabel>
                    <HealthValue>{card.healthData.unknown}</HealthValue>
                  </HealthItem>
                </HealthBreakdown>
              </MeasureCard>
            );
          }

          return (
            <MeasureCard key={card.id} $clickable={false}>
              <MeasureHeader>
                <MeasureTitle>{card.name}</MeasureTitle>
                <Tooltip
                  variant="black"
                  label={tSimple(`strategy.overview.tooltips.${card.id}`, {
                    work_units: i18n.t('common.work_units'),
                    work_unit: i18n.t('common.work_unit'),
                    entity: entityName,
                  })}
                  zIndex={1000}
                >
                  <Icon name="info" size={16} color={skyDark} style={{ cursor: 'help' }} />
                </Tooltip>
              </MeasureHeader>
              <MeasureDescription>{card.description}</MeasureDescription>
              <MeasureValueContainer>
                <MeasureValue>{card.value}</MeasureValue>
              </MeasureValueContainer>
            </MeasureCard>
          );
        })}
      </MeasuresGrid>
    </SectionContainer>
  );
}

const SectionContainer = styled.div`
  background-color: white;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MeasuresGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-top: 8px;
`;

const MeasureCard = styled.div<{ $clickable: boolean }>`
  background-color: ${skyTight};
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 16px;
  min-height: 120px;
  height: auto;
  border: 1px solid ${skyLight};
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
  transition: all 0.2s ease-in-out;
`;

const MeasureHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
`;

const MeasureTitle = styled.div`
  font-family: 'Figtree', sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
  color: #000000;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const MeasureDescription = styled.div`
  font-family: 'Figtree', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 14px;
  color: ${skyDark};
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const MeasureValueContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const MeasureValue = styled.div`
  font-family: 'Figtree', sans-serif;
  font-size: 32px;
  font-weight: 700;
  line-height: 36px;
  color: #000000;
`;

const HealthBreakdown = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  width: 100%;
`;

const HealthItem = styled.div<{ color: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px;
  border-radius: 8px;
  background-color: ${(props) => `${props.color}15`};
`;

const HealthLabel = styled.div`
  font-family: 'Figtree', sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: ${skyDark};
  text-align: center;
`;

const HealthValue = styled.div`
  font-family: 'Figtree', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #000000;
`;
